import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import Button from 'react-bootstrap/Button';
import { useIsMount } from '../../../hooks/useIsMountHook';
import BzmIcon from '../../icons/BzmIcon';

interface InputListProps<T> {
  list: T[];
  renderItem: React.ComponentType<{
    item: T;
    onChange: (item: T) => void;
    showLabel: boolean;
    label?: string;
    disabled?: boolean;
    metadata?: any;
  }>; // Custom component for rendering an item
  disabled?: boolean;
  onAddNewItem: () => void;
  onListChange: (list: T[]) => void;
  emptyListMessage: string;
  label: string;
  metadata?: any;
}

/**
 * Generic list of components.
 * @param list - components to display in the list
 * @param ItemComponent - component used for rendering the item of the list
 * @param disabled - adding / removing / editing of the items
 * @param onAddNewItem - function for adding new item to the list
 * @param onListChange - when internal list changes, propagate it up
 * @param emptyListMessage - shown when list is empty
 * @param label - optional label for the list
 * @constructor
 */
const ComponentList = <T,>({
  list,
  renderItem: ItemComponent,
  disabled,
  onAddNewItem,
  onListChange,
  emptyListMessage,
  label,
  metadata,
}: InputListProps<T>) => {
  const [items, setItems] = useState([...list]);

  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount && !_.isEqual(items, list)) {
      setItems([...list]);
    }
  }, [list]);

  useEffect(() => {
    if (!isMount && !_.isEqual(items, list)) {
      onListChange(items);
    }
  }, [items]);

  const handleRemove = (index: number) => {
    const update = _.cloneDeep(items);
    update.splice(index, 1);
    setItems(update);
  };

  const isEmpty = _.size(items) === 0;

  return (
    <div>
      {_.map(items, (item, index) => {
        const isFirst = index === 0;
        return (
          <div className={'d-flex mb-4'} key={'component-key-' + index}>
            <div style={{ flexGrow: 1 }}>
              <ItemComponent
                item={item}
                showLabel={index === 0}
                onChange={item => {
                  // Update current item in the list based on index
                  const updated = _.cloneDeep(items);
                  updated[index] = item;
                  setItems(updated);
                }}
                disabled={disabled}
                label={isFirst && label}
                metadata={metadata}
              />
            </div>
            <div className={'ml-4'} style={{ minWidth: 72 }}>
              {index === 0 && <div className={'mb-5'}> </div>}
              <Button
                disabled={disabled}
                onClick={() => handleRemove(index)}
                className="btn-circular"
                title={'Add New'}
                variant="danger"
              >
                <BzmIcon variant={'icon-bzm-delete'} />
              </Button>

              {index === _.size(items) - 1 && (
                <Button
                  disabled={disabled}
                  onClick={() => onAddNewItem()}
                  className="btn-circular ml-3"
                  title={'Add New'}
                  variant="outline-primary"
                >
                  <BzmIcon variant={'icon-bzm-add'} />
                </Button>
              )}
            </div>
          </div>
        );
      })}
      {isEmpty && (
        <>
          <p>{emptyListMessage}</p>
          <Button
            disabled={disabled}
            onClick={() => onAddNewItem()}
            className="btn-circular"
            title={'Add New'}
            variant="primary"
          >
            <BzmIcon variant={'icon-bzm-add'} />
          </Button>
        </>
      )}
    </div>
  );
};

export default ComponentList;
